import * as React from "react";
import {
  Card,
  Body2,
  Caption1,
  Button,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import { ArrowUndo24Regular } from "@fluentui/react-icons";
import { AlertEntry } from "../../services/workbookAnalyticsRulesEngine";

const useStyles = makeStyles({
  card: {
    marginBottom: "8px",
    width: "100%",
    backgroundColor: tokens.colorNeutralBackground3,
    opacity: 0.7,
    padding: "8px 12px",
  },
  content: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "8px",
  },
  textContent: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    flex: 1,
    minWidth: 0,
  },
  firstLine: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    flexWrap: "wrap",
  },
  code: {
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground3,
  },
  dismissalInfo: {
    color: tokens.colorNeutralForeground3,
    fontStyle: "italic",
  },
  description: {
    color: tokens.colorNeutralForeground3,
    marginLeft: "4px",
  },
  restoreButton: {
    minWidth: "auto",
    flexShrink: 0,
  },
});

interface DismissedAlertCardProps {
  alert: AlertEntry;
  onRestore?: (alertId: string) => void;
}

const DismissedAlertCard: React.FC<DismissedAlertCardProps> = ({ alert, onRestore }) => {
  const styles = useStyles();

  if (!alert.dismissal) {
    return null;
  }

  const reasonText = alert.dismissal.reasonCode.replace(/_/g, " ");
  const description = alert.dismissal.context && typeof alert.dismissal.context === "object" && "description" in alert.dismissal.context
    ? (alert.dismissal.context as { description?: string }).description
    : undefined;

  return (
    <Card className={styles.card} size="small">
      <div className={styles.content}>
        <div className={styles.textContent}>
          <div className={styles.firstLine}>
            <Body2 className={styles.code}>{alert.code}</Body2>
            <Caption1 className={styles.dismissalInfo}>
              {reasonText}
              {description && <span className={styles.description}> - {description}</span>}
            </Caption1>
          </div>
        </div>
        {onRestore && (
          <Button
            appearance="subtle"
            size="small"
            icon={<ArrowUndo24Regular />}
            className={styles.restoreButton}
            onClick={() => onRestore(alert.id)}
            title="Restore alert"
          />
        )}
      </div>
    </Card>
  );
};

export default DismissedAlertCard;
